<template>
  <section class="article-full-container">
    <div class="container">
      <div class="article-full-wrapper">
        <TableOfContents :headings="headings" />

        <article class="article-full-content">

          <!-- Header -->
          <div class="article-full-header">
            <span :class="['article-category', article.category]">{{ article.categoryText }}</span>
            <h1 class="article-full-title">{{ article.title }}</h1>
            <div class="article-full-meta">
              <div class="article-full-author">
                <img :src="article.authorAvatar" :alt="article.author" class="author-avatar">
                <div class="author-info">
                  <span class="author-name">{{ article.author }}</span>
                  <span class="author-role">Auteur</span>
                </div>
              </div>
              <div class="meta-divider"></div>
              <div class="meta-badges">
                <div class="meta-badge">
                  <component :is="SvgCalendar" class="meta-icon" />
                  {{ article.date }}
                </div>
                <div class="meta-badge">
                  <component :is="SvgClock" class="meta-icon" />
                  {{ article.readingTime }} de lecture
                </div>
              </div>
            </div>
          </div>

          <!-- Hero image -->
          <div class="article-img-wrapper">
            <img :src="article.img" :alt="article.title" class="article-full-img">
            <div class="article-img-overlay"></div>
          </div>

          <!-- Body -->
          <div class="article-body" id="articleBody" v-html="article.content"></div>

          <!-- Share -->
          <div class="share-article">
            <div class="share-header">
              <div class="share-line"></div>
              <span class="share-label">Partager</span>
              <div class="share-line"></div>
            </div>
            <div class="share-buttons">
              <a href="#" class="share-btn twitter" @click.prevent="shareOnTwitter">
                <component :is="SvgTwitter" />
                <span>Twitter</span>
              </a>
              <a href="#" class="share-btn linkedin" @click.prevent="shareOnLinkedIn">
                <component :is="SvgLinkedin" />
                <span>LinkedIn</span>
              </a>
              <a href="#" class="share-btn facebook" @click.prevent="shareOnFacebook">
                <component :is="SvgFacebook" />
                <span>Facebook</span>
              </a>
            </div>
          </div>

          <!-- Back -->
          <button class="back-to-list" @click="goBack">
            <component :is="SvgArrowLeft" />
            Retour aux articles
          </button>

        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, h } from 'vue'
import TableOfContents from './TableOfContents.vue'

// Import Prism.js
import Prism from 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'

// Import des langages
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-markup'

// ── SVG helper ────────────────────────────────────────────────────────────────
const icon = (children) => defineComponent({
  render: () => h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '1.8',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
  }, children)
})

const SvgCalendar = icon([
  h('rect', { x: '3', y: '4', width: '18', height: '18', rx: '2', ry: '2' }),
  h('line', { x1: '16', y1: '2',  x2: '16', y2: '6'  }),
  h('line', { x1: '8',  y1: '2',  x2: '8',  y2: '6'  }),
  h('line', { x1: '3',  y1: '10', x2: '21', y2: '10' }),
])

const SvgClock = icon([
  h('circle',   { cx: '12', cy: '12', r: '10' }),
  h('polyline', { points: '12 6 12 12 16 14' }),
])

const SvgArrowLeft = icon([
  h('line',     { x1: '19', y1: '12', x2: '5',  y2: '12' }),
  h('polyline', { points: '12 19 5 12 12 5' }),
])

const SvgTwitter = defineComponent({
  render: () => h('svg', { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', fill: 'currentColor' }, [
    h('path', { d: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z' }),
  ])
})

const SvgLinkedin = defineComponent({
  render: () => h('svg', { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', fill: 'currentColor' }, [
    h('path', { d: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' }),
  ])
})

const SvgFacebook = defineComponent({
  render: () => h('svg', { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', fill: 'currentColor' }, [
    h('path', { d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' }),
  ])
})

export default {
  name: 'ArticleFull',
  components: { TableOfContents },

  props: {
    article: { 
      type: Object, 
      required: true,
      default: () => ({})
    }
  },

  data() {
    return {
      headings: [],
      SvgCalendar,
      SvgClock,
      SvgArrowLeft,
      SvgTwitter,
      SvgLinkedin,
      SvgFacebook,
      observer: null,
      highlightTimer: null,
      resizeTimer: null
    }
  },

  mounted() {
    this.extractHeadings()
    this.$nextTick(() => { 
      this.setupCodeBlocks()
      this.setupResponsiveCode()
      this.setupMutationObserver()
      this.scheduleHighlight()
    })
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer)
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    extractHeadings() {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = this.article.content
      const headings = tempDiv.querySelectorAll('h2, h3')
      this.headings = Array.from(headings).map((heading, index) => {
        if (!heading.id) heading.id = `section-${index}`
        return {
          id: heading.id,
          text: heading.textContent,
          tag: heading.tagName,
          parentId: this.findParentH2(heading, headings)
        }
      })
    },

    findParentH2(heading, headings) {
      if (heading.tagName === 'H2') return null
      const index = Array.from(headings).indexOf(heading)
      for (let i = index - 1; i >= 0; i--) {
        if (headings[i].tagName === 'H2') return headings[i].id
      }
      return null
    },

    setupMutationObserver() {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            this.scheduleHighlight()
          }
        })
      })

      const articleBody = document.getElementById('articleBody')
      if (articleBody) {
        this.observer.observe(articleBody, {
          childList: true,
          subtree: true
        })
      }
    },

    scheduleHighlight() {
      if (this.highlightTimer) {
        clearTimeout(this.highlightTimer)
      }
      this.highlightTimer = setTimeout(() => {
        this.highlightCodeBlocks()
      }, 100)
    },

    highlightCodeBlocks() {
      console.log('Highlighting code blocks...')
      const blocks = document.querySelectorAll('#articleBody pre code')
      console.log('Found blocks:', blocks.length)
      
      blocks.forEach(block => {
        block.classList.remove('prism-highlighted')
        Prism.highlightElement(block)
        block.classList.add('prism-highlighted')
        console.log('Highlighted block with classes:', block.className)
      })
    },

    setupResponsiveCode() {
      const adjustCodeFontSize = () => {
        const isMobile = window.innerWidth <= 768
        const codeBlocks = document.querySelectorAll('#articleBody pre code')
        
        codeBlocks.forEach(block => {
          if (isMobile) {
            block.style.fontSize = '0.75rem'
          } else {
            block.style.fontSize = ''
          }
        })
      }

      // Initial adjustment
      adjustCodeFontSize()

      // Debounced resize handler
      this.handleResize = () => {
        clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(adjustCodeFontSize, 150)
      }

      window.addEventListener('resize', this.handleResize)
    },

    setupCodeBlocks() {
      const blocks = document.querySelectorAll('#articleBody pre')
      blocks.forEach(pre => {
        if (pre.parentElement.classList.contains('code-card')) return

        const code = pre.querySelector('code')
        if (!code) return

        // Détection du langage
        let langClass = 'javascript'
        if (code.className) {
          const match = code.className.match(/language-(\w+)/)
          if (match) langClass = match[1]
        }

        // Détection spécifique Python
        if (code.innerText.includes('def ') || 
            code.innerText.includes('import ') || 
            code.innerText.includes('class ') ||
            code.innerText.includes('try:') || 
            code.innerText.includes('except:')) {
          langClass = 'python'
        }

        if (!code.className.includes(`language-${langClass}`)) {
          code.className += ` language-${langClass}`
        }

        const extMap = {
          python: 'py',
          javascript: 'js', 
          typescript: 'ts',
          bash: 'sh', 
          css: 'css', 
          html: 'html', 
          json: 'json'
        }
        
        const ext = extMap[langClass] || langClass
        const langLabel = langClass.charAt(0).toUpperCase() + langClass.slice(1)
        const filename = `main.${ext}`

        // Création de la carte de code
        const card = document.createElement('div')
        card.className = 'code-card'

        const header = document.createElement('div')
        header.className = 'code-header'
        
        // Bouton wrap pour mobile
        const wrapButton = `
          <button class="code-wrap-toggle" title="Activer le retour à la ligne">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M4 12h16M4 12l4-4M4 12l4 4M20 12l-4-4M20 12l-4 4"/>
            </svg>
          </button>
        `

        header.innerHTML = `
          <div class="dot dot-r"></div>
          <div class="dot dot-y"></div>
          <div class="dot dot-g"></div>
          <span class="code-filename">${filename} — ${langLabel}</span>
          <div class="code-actions">
            ${wrapButton}
            <button class="copy-btn" title="Copier le code">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        `

        pre.parentNode.insertBefore(card, pre)
        card.appendChild(header)
        card.appendChild(pre)

        // Gestionnaire pour le bouton wrap
        const wrapBtn = header.querySelector('.code-wrap-toggle')
        if (wrapBtn) {
          wrapBtn.addEventListener('click', () => {
            pre.classList.toggle('wrapped')
            const isWrapped = pre.classList.contains('wrapped')
            wrapBtn.title = isWrapped ? 'Désactiver le retour à la ligne' : 'Activer le retour à la ligne'
            
            // Ajuster l'icône
            if (isWrapped) {
              wrapBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M4 12h16M4 12l4 4M4 12l4-4M20 12l-4 4M20 12l-4-4"/>
                </svg>
              `
            } else {
              wrapBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M4 12h16M4 12l4-4M4 12l4 4M20 12l-4-4M20 12l-4 4"/>
                </svg>
              `
            }
          })
        }

        // Bouton de copie
        const copyBtn = header.querySelector('.copy-btn')
        copyBtn.addEventListener('click', () => {
          const text = code.innerText || pre.innerText
          navigator.clipboard.writeText(text).then(() => {
            copyBtn.classList.add('copied')
            copyBtn.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            `
            setTimeout(() => {
              copyBtn.classList.remove('copied')
              copyBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              `
            }, 2000)
          })
        })
      })

      this.scheduleHighlight()
    },

    goBack() { 
      this.$router.push('/') 
    },

    shareOnTwitter() {
      window.open(`https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(this.article.title)}`, '_blank')
    },
    
    shareOnLinkedIn() {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')
    },
    
    shareOnFacebook() {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')
    }
  },

  watch: {
    'article.content': {
      handler() {
        this.extractHeadings()
        this.$nextTick(() => {
          this.setupCodeBlocks()
          this.setupResponsiveCode()
        })
      },
      deep: true
    }
  }
}
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────────── */
.article-full-container {
  padding: clamp(32px, 5vw, 72px) 0 clamp(48px, 7vw, 96px);
  background: #f0f4f9;
  min-height: 100vh;
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 clamp(16px, 4vw, 40px);
}

.article-full-wrapper {
  display: flex;
  gap: clamp(20px, 3vw, 48px);
  position: relative;
  align-items: flex-start;
}

.article-full-content {
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border-radius: clamp(16px, 2vw, 20px);
  padding: clamp(24px, 4vw, 52px) clamp(20px, 4vw, 56px);
  box-shadow: 0 1px 3px rgba(0,0,0,.04), 0 8px 32px rgba(0,0,0,.06);
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.article-full-header {
  margin-bottom: clamp(24px, 4vw, 40px);
}

.article-category {
  display: inline-block;
  padding: 4px clamp(12px, 2vw, 14px);
  border-radius: 100px;
  font-size: clamp(0.7rem, 2vw, 0.75rem);
  font-weight: 700;
  letter-spacing: .07em;
  text-transform: uppercase;
  margin-bottom: clamp(12px, 2vw, 18px);
  background: #eef2ff;
  color: #4f46e5;
}

.article-full-title {
  font-size: clamp(1.5rem, 5vw, 2.6rem);
  font-weight: 800;
  line-height: 1.25;
  color: #0f172a;
  letter-spacing: -0.03em;
  margin-bottom: clamp(20px, 3vw, 28px);
  word-break: break-word;
}

.article-full-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: clamp(12px, 2vw, 20px);
}

.article-full-author {
  display: flex;
  align-items: center;
  gap: clamp(8px, 1.5vw, 12px);
}

.author-avatar {
  width: clamp(36px, 5vw, 44px);
  height: clamp(36px, 5vw, 44px);
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  box-shadow: 0 0 0 3px #f8fafc;
}

.author-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.author-name {
  font-weight: 700;
  font-size: clamp(0.85rem, 2vw, 0.9rem);
  color: #0f172a;
}

.author-role {
  font-size: clamp(0.7rem, 1.8vw, 0.75rem);
  color: #94a3b8;
}

.meta-divider {
  width: 1px;
  height: clamp(24px, 4vw, 32px);
  background: #e2e8f0;
}

.meta-badges {
  display: flex;
  gap: clamp(12px, 2vw, 16px);
  flex-wrap: wrap;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: clamp(4px, 1vw, 6px);
  font-size: clamp(0.75rem, 2vw, 0.82rem);
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
}

.meta-icon {
  width: clamp(13px, 2vw, 15px);
  height: clamp(13px, 2vw, 15px);
  color: #94a3b8;
  flex-shrink: 0;
}

/* ── Hero image ──────────────────────────────────────────────────────────── */
.article-img-wrapper {
  position: relative;
  border-radius: clamp(12px, 2vw, 16px);
  overflow: hidden;
  margin-bottom: clamp(28px, 4vw, 44px);
  box-shadow: 0 4px 24px rgba(0,0,0,.10);
}

.article-full-img {
  width: 100%;
  height: clamp(200px, 40vw, 420px);
  object-fit: cover;
  display: block;
  transition: transform .6s ease;
}

.article-img-wrapper:hover .article-full-img {
  transform: scale(1.02);
}

.article-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 55%, rgba(15,23,42,.18));
  pointer-events: none;
}

/* ── Article body ────────────────────────────────────────────────────────── */
.article-body {
  font-size: clamp(0.95rem, 2.5vw, 1.07rem);
  line-height: 1.85;
  color: #334155;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.article-body >>> h2 {
  font-size: clamp(1.4rem, 4vw, 1.65rem);
  font-weight: 800;
  margin: clamp(28px, 5vw, 52px) 0 clamp(12px, 2vw, 18px);
  color: #0f172a;
  letter-spacing: -.02em;
  padding-top: clamp(20px, 3vw, 28px);
  border-top: 1px solid #f1f5f9;
  word-break: break-word;
}

.article-body >>> h3 {
  font-size: clamp(1.1rem, 3vw, 1.25rem);
  font-weight: 700;
  margin: clamp(24px, 4vw, 36px) 0 clamp(10px, 1.5vw, 14px);
  color: #1e293b;
  word-break: break-word;
}

.article-body >>> p { 
  margin-bottom: clamp(16px, 2.5vw, 22px); 
}

.article-body >>> ul,
.article-body >>> ol {
  margin-bottom: clamp(16px, 2.5vw, 22px);
  padding-left: clamp(20px, 4vw, 28px);
}

.article-body >>> li { margin-bottom: clamp(4px, 1vw, 8px); }

.article-body >>> a {
  color: #4f46e5;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: #c7d2fe;
  transition: text-decoration-color .2s;
  word-break: break-word;
}
.article-body >>> a:hover { text-decoration-color: #4f46e5; }

/* Inline code — One Dark Pro */
.article-body >>> code {
  background: #2c313a;
  color: #e5c07b;
  padding: .2em .48em;
  border-radius: 6px;
  font-family: "JetBrains Mono", "Fira Code", Consolas, monospace;
  font-size: 0.875em;
  border: 1px solid #3e4451;
  white-space: normal;
  word-break: break-word;
}

/* ── Code card responsive ───────────────────────────────────────────────── */
.article-body >>> .code-card {
  border-radius: clamp(8px, 1.5vw, 14px);
  overflow: hidden;
  margin: clamp(1.2em, 3vw, 2em) 0;
  box-shadow: 0 0 0 1px #21252b, 0 4px 20px rgba(0,0,0,.4);
  max-width: 100%;
  width: 100%;
}

.article-body >>> .code-header {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #21252b;
  padding: 8px 12px;
  border-bottom: 1px solid #181a1f;
  flex-wrap: wrap;
}

.article-body >>> .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: filter .2s;
}

.article-body >>> .code-card:hover .dot { filter: brightness(1.15); }
.article-body >>> .dot-r { background: #ff5f57; }
.article-body >>> .dot-y { background: #febc2e; }
.article-body >>> .dot-g { background: #28c840; }

.article-body >>> .code-filename {
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: clamp(0.7rem, 1.8vw, 0.75rem);
  color: #5c6370;
  margin-left: 6px;
  flex: 1;
  user-select: none;
  letter-spacing: .02em;
  min-width: 120px;
  word-break: break-word;
}

.article-body >>> .code-actions {
  display: flex;
  gap: 4px;
  margin-left: auto;
}

.article-body >>> .code-wrap-toggle {
  background: transparent;
  border: none;
  color: #5c6370;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}

.article-body >>> .code-wrap-toggle:hover {
  color: #abb2bf;
  background: rgba(255,255,255,0.1);
}

.article-body >>> .code-wrap-toggle svg {
  width: 14px;
  height: 14px;
}

.article-body >>> .copy-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #4b5263;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: color .2s, background .2s;
}

.article-body >>> .copy-btn svg { 
  width: clamp(13px, 2vw, 15px); 
  height: clamp(13px, 2vw, 15px); 
  display: block; 
}

.article-body >>> .copy-btn:hover {
  color: #abb2bf;
  background: rgba(255,255,255,.07);
}

.article-body >>> .copy-btn.copied { color: #98c379; }

/* ── Pre block responsive — One Dark Pro ────────────────────────────────── */
.article-body >>> pre {
  background: #282c34;
  color: #abb2bf;
  padding: clamp(0.8em, 3vw, 1.5em) clamp(0.8em, 3vw, 1.75em);
  margin: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  font-family: "JetBrains Mono", "Fira Code", Consolas, monospace;
  font-size: clamp(0.75rem, 2.5vw, 0.9rem);
  line-height: 1.6;
  border-radius: 0;
  border: none;
  box-shadow: none;
  max-width: 100%;
  width: 100%;
  white-space: pre;
  word-wrap: normal;
  -ms-overflow-style: -ms-autohiding-scrollbar;
}

.article-body >>> pre code {
  background: transparent;
  color: inherit;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 0;
  font-size: inherit;
  white-space: pre;
  min-width: min-content;
  display: inline-block;
  min-width: 100%;
}

/* Style pour le code avec retour à la ligne */
.article-body >>> pre.wrapped {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.article-body >>> pre.wrapped code {
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Scrollbar personnalisée */
.article-body >>> pre::-webkit-scrollbar {
  height: 4px;
  background: #21252b;
}

.article-body >>> pre::-webkit-scrollbar-track {
  background: #21252b;
  border-radius: 2px;
}

.article-body >>> pre::-webkit-scrollbar-thumb {
  background: #528bff;
  border-radius: 2px;
}

.article-body >>> pre::-webkit-scrollbar-thumb:hover {
  background: #6c9eff;
}

/* ── Syntax tokens — One Dark Pro ────────────────────────────────────────── */
.article-body >>> .token.keyword { color: #c678dd; }
.article-body >>> .token.function { color: #61afef; }
.article-body >>> .token.string { color: #98c379; }
.article-body >>> .token.comment { color: #5c6370; font-style: italic; }
.article-body >>> .token.number { color: #d19a66; }
.article-body >>> .token.operator { color: #56b6c2; }
.article-body >>> .token.class-name { color: #e5c07b; }
.article-body >>> .token.boolean { color: #d19a66; }

/* ── Blockquote ──────────────────────────────────────────────────────────── */
.article-body >>> blockquote {
  border-left: 3px solid #528bff;
  background: #f0f4ff;
  padding: clamp(14px, 3vw, 18px) clamp(18px, 4vw, 24px);
  margin: clamp(20px, 4vw, 28px) 0;
  border-radius: 0 clamp(8px, 1.5vw, 12px) clamp(8px, 1.5vw, 12px) 0;
  font-style: italic;
  color: #475569;
}
.article-body >>> blockquote p { margin-bottom: 0; }

.article-body >>> img {
  max-width: 100%;
  height: auto;
  border-radius: clamp(8px, 1.5vw, 12px);
  margin: clamp(20px, 4vw, 28px) 0;
  box-shadow: 0 4px 16px rgba(0,0,0,.08);
}

/* ── Share ───────────────────────────────────────────────────────────────── */
.share-article { 
  margin-top: clamp(40px, 8vw, 64px); 
}

.share-header {
  display: flex;
  align-items: center;
  gap: clamp(12px, 2vw, 16px);
  margin-bottom: clamp(20px, 3vw, 24px);
}

.share-line { flex: 1; height: 1px; background: #e2e8f0; }

.share-label {
  font-size: clamp(0.7rem, 2vw, 0.78rem);
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: #94a3b8;
  white-space: nowrap;
}

.share-buttons { 
  display: flex; 
  gap: clamp(10px, 2vw, 12px); 
  flex-wrap: wrap; 
}

.share-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: clamp(6px, 1.5vw, 9px);
  padding: clamp(8px, 2vw, 10px) clamp(16px, 3vw, 20px);
  border-radius: 100px;
  text-decoration: none;
  font-size: clamp(0.8rem, 2.2vw, 0.875rem);
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform .2s, box-shadow .2s, opacity .2s;
  flex: 0 1 auto;
}

.share-btn svg { 
  width: clamp(14px, 2.5vw, 16px); 
  height: clamp(14px, 2.5vw, 16px); 
  flex-shrink: 0; 
}

.share-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,.18);
  opacity: .92;
}

.share-btn.twitter  { background: #000;    color: #fff; }
.share-btn.linkedin { background: #0a66c2; color: #fff; }
.share-btn.facebook { background: #1877f2; color: #fff; }

/* ── Back button ─────────────────────────────────────────────────────────── */
.back-to-list {
  display: inline-flex;
  align-items: center;
  gap: clamp(8px, 1.5vw, 10px);
  background: transparent;
  color: #4f46e5;
  padding: clamp(10px, 2vw, 12px) 0;
  border: none;
  font-size: clamp(0.85rem, 2.2vw, 0.9rem);
  font-weight: 600;
  margin-top: clamp(32px, 6vw, 44px);
  cursor: pointer;
  transition: gap .2s;
}

.back-to-list svg { 
  width: clamp(16px, 2.5vw, 18px); 
  height: clamp(16px, 2.5vw, 18px); 
  transition: transform .2s; 
}
.back-to-list:hover svg { transform: translateX(-4px); }
.back-to-list:hover { gap: clamp(10px, 2vw, 14px); }

/* ── Responsive ──────────────────────────────────────────────────────────── */
@media (max-width: 992px) {
  .article-full-wrapper { 
    flex-direction: column; 
  }
}

@media (max-width: 768px) {
  .article-full-meta {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .meta-divider { 
    display: none; 
  }
  
  .share-buttons { 
    flex-direction: column; 
    width: 100%;
  }
  
  .share-btn { 
    justify-content: center; 
    border-radius: 12px;
    width: 100%;
  }
  
  .article-body >>> .code-header {
    padding: 6px 10px;
    gap: 4px;
  }
  
  .article-body >>> .dot {
    width: 8px;
    height: 8px;
  }
  
  .article-body >>> .code-filename {
    font-size: 0.7rem;
    min-width: auto;
    margin-left: 2px;
  }
  
  .article-body >>> pre {
    font-size: 0.75rem;
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .article-full-content {
    padding: 20px 16px;
  }
  
  .meta-badges {
    flex-direction: column;
    gap: 8px;
  }
  
  .meta-badge {
    white-space: normal;
  }
  
  .article-body >>> h2 {
    font-size: 1.3rem;
  }
  
  .article-body >>> h3 {
    font-size: 1.1rem;
  }
  
  .article-body >>> .code-header {
    padding: 6px 8px;
  }
  
  .article-body >>> .dot {
    width: 6px;
    height: 6px;
  }
  
  .article-body >>> .code-filename {
    font-size: 0.65rem;
  }
  
  .article-body >>> .code-wrap-toggle svg,
  .article-body >>> .copy-btn svg {
    width: 14px;
    height: 14px;
  }
  
  .article-body >>> pre {
    font-size: 0.7rem;
    padding: 10px;
  }
  
  /* Permettre le zoom sur mobile pour les longs blocs de code */
  .article-body >>> pre {
    -webkit-text-size-adjust: 100%;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .share-btn:hover {
    transform: none;
    box-shadow: none;
  }
  
  .back-to-list:hover svg {
    transform: none;
  }
  
  .article-body >>> a {
    -webkit-tap-highlight-color: rgba(79, 70, 229, 0.1);
  }
  
  .article-body >>> .copy-btn,
  .article-body >>> .code-wrap-toggle {
    padding: 8px 10px;
  }
}
</style>